import React from 'react';
import { useTranslation } from 'react-i18next';
import { IoHelpCircle as HelpIcon } from 'react-icons/io5';

export default function CropTool({
  cropSettings,
  handleCropValueChange,
  applyCrop,
  isProcessing,
  showToolTip
}) {
  const { t } = useTranslation();
  return (
    <div className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-500 overflow-hidden border border-gray-100">
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">{t('crop.title')}</h3>
          <div className="flex items-center gap-2">
            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">{t('crop.livePreview')}</span>
            <button
              onMouseEnter={(e) => showToolTip(t('crop.tooltip'), e)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <HelpIcon size={16} />
            </button>
          </div>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('crop.width.label')}</label>
              <input
                type="range"
                min="10"
                max="100"
                value={cropSettings.width}
                onChange={(e) => handleCropValueChange('width', e.target.value)}
                className="w-full h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-blue-600"
                aria-label={t('crop.width.aria')}
              />
              <div className="flex justify-between items-center mt-2">
                <input
                  type="number"
                  min="10"
                  max="100"
                  value={cropSettings.width}
                  onChange={(e) => handleCropValueChange('width', e.target.value)}
                  onBlur={applyCrop}
                  className="w-16 p-1.5 border border-gray-300 rounded text-center text-sm"
                  aria-label={t('crop.width.numberAria')}
                />
                <span className="text-xs text-gray-500">{t('crop.width.maxHint')}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('crop.height.label')}</label>
              <input
                type="range"
                min="10"
                max="100"
                value={cropSettings.height}
                onChange={(e) => handleCropValueChange('height', e.target.value)}
                className="w-full h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-blue-600"
                aria-label={t('crop.height.aria')}
              />
              <div className="flex justify-between items-center mt-2">
                <input
                  type="number"
                  min="10"
                  max="100"
                  value={cropSettings.height}
                  onChange={(e) => handleCropValueChange('height', e.target.value)}
                  onBlur={applyCrop}
                  className="w-16 p-1.5 border border-gray-300 rounded text-center text-sm"
                  aria-label={t('crop.height.numberAria')}
                />
                <span className="text-xs text-gray-500">{t('crop.height.maxHint')}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('crop.x.label')}</label>
              <input
                type="range"
                min="0"
                max={100 - cropSettings.width}
                value={cropSettings.x}
                onChange={(e) => handleCropValueChange('x', e.target.value)}
                className="w-full h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-blue-600"
                aria-label={t('crop.x.aria')}
              />
              <div className="flex justify-between items-center mt-2">
                <input
                  type="number"
                  min="0"
                  max={100 - cropSettings.width}
                  value={cropSettings.x}
                  onChange={(e) => handleCropValueChange('x', e.target.value)}
                  onBlur={applyCrop}
                  className="w-16 p-1.5 border border-gray-300 rounded text-center text-sm"
                  aria-label={t('crop.x.numberAria')}
                />
                <span className="text-xs text-gray-500">{t('crop.x.maxHint', { value: 100 - cropSettings.width })}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('crop.y.label')}</label>
              <input
                type="range"
                min="0"
                max={100 - cropSettings.height}
                value={cropSettings.y}
                onChange={(e) => handleCropValueChange('y', e.target.value)}
                className="w-full h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-blue-600"
                aria-label={t('crop.y.aria')}
              />
              <div className="flex justify-between items-center mt-2">
                <input
                  type="number"
                  min="0"
                  max={100 - cropSettings.height}
                  value={cropSettings.y}
                  onChange={(e) => handleCropValueChange('y', e.target.value)}
                  onBlur={applyCrop}
                  className="w-16 p-1.5 border border-gray-300 rounded text-center text-sm"
                  aria-label={t('crop.y.numberAria')}
                />
                <span className="text-xs text-gray-500">{t('crop.y.maxHint', { value: 100 - cropSettings.height })}</span>
              </div>
            </div>
          </div>
        </div>

        <div>
          <button
            onClick={applyCrop}
            disabled={isProcessing}
            className={`w-full py-3 rounded-lg transition-all duration-300 font-medium ${
              isProcessing ? 'bg-blue-400 text-white cursor-not-allowed' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
            }`}
          >
            {isProcessing ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                {t('common.processing')}
              </div>
            ) : t('crop.confirm')
            }
          </button>
        </div>
      </div>
    </div>
  );
}